import { useState } from 'react';
import { bt, btp } from '../../i18n';
import type { LinkField, LinkValue } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { EntityPickerListbox, type PickerSource } from './EntityPickerListbox';
import {
  collectionLinkPickerSource,
  pageLinkPickerSource,
  productLinkPickerSource,
} from '../../lib/linkPickers';

/**
 * Link control (build-plan §6.3). "Link to" selector (Product / Collection / Page / External) + a
 * target sub-control, optional label + open-in-new-tab toggle.
 *
 *  - product / collection / page are PICKED from the seller's own catalog — a searchable listbox over the
 *    real entities, keyed by the SLUG `ctx.resolveUrl` builds the URL from (see lib/linkPickers.ts). This
 *    replaces the M4 stub, a bare "product handle (e.g. my-product)" box the seller had to type from
 *    memory with no way to know whether it existed (founder, 2026-08-27).
 *  - external uses a STRICT scheme allowlist: http/https/mailto only — javascript:/data: are rejected
 *    (the XSS vector). Commits immediately on each edit.
 */
type LinkType = LinkValue['type'];

const SAFE_SCHEME = /^(https?:|mailto:)/i;

/** Everything the picker needs to name what the seller is choosing, per link type. */
const PICKERS: Record<
  'product' | 'collection' | 'page',
  { source: PickerSource; search: string; empty: string; error: string }
> = {
  product: {
    source: productLinkPickerSource,
    search: bt('chrome.link.search_products', 'Search products…'),
    empty: bt('chrome.link.no_products', 'No products found.'),
    error: bt('chrome.link.products_failed', 'Couldn’t load products.'),
  },
  collection: {
    source: collectionLinkPickerSource,
    search: bt('chrome.link.search_collections', 'Search collections…'),
    empty: bt('chrome.link.no_collections', 'No collections found.'),
    error: bt('chrome.link.collections_failed', 'Couldn’t load collections.'),
  },
  page: {
    source: pageLinkPickerSource,
    search: bt('chrome.link.search_pages', 'Search pages…'),
    empty: bt('chrome.link.no_pages', 'No published pages.'),
    error: bt('chrome.link.pages_failed', 'Couldn’t load pages.'),
  },
};

function isSafeExternal(url: string): boolean {
  if (url.trim() === '') return true; // empty is allowed (incomplete), just not committed as a target
  try {
    const u = new URL(url);
    return SAFE_SCHEME.test(u.protocol);
  } catch {
    // relative URL (starts with /) is fine; anything else is rejected
    return url.startsWith('/');
  }
}

export function LinkControl({
  field,
  value,
  onChange,
}: {
  field: LinkField;
  value: LinkValue | undefined;
  onChange: (next: LinkValue) => void;
}) {
  const current: LinkValue = value ?? { type: 'external', url: '' };
  const [externalError, setExternalError] = useState<string | undefined>();

  const setType = (type: LinkType) => {
    const base = { label: current.label, newTab: current.newTab };
    switch (type) {
      case 'product':
        onChange({ type: 'product', slug: '', ...base });
        break;
      case 'collection':
        onChange({ type: 'collection', slug: '', ...base });
        break;
      case 'page':
        onChange({ type: 'page', slug: '', ...base });
        break;
      case 'external':
        onChange({ type: 'external', url: '', ...base });
        break;
    }
  };

  const labelInputId = `${field.id}-label`;

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span">
      <div className="bld-link">
        {/* DESTINATION — the four type tabs and the target they configure are ONE widget (joined
            borders, no gap), not two unrelated controls stacked: the tab row reads as the header of
            the box underneath it. Before this, "URL" sat above a bare text box that looked exactly
            like the label box below it (founder, 2026-09-18). */}
        <div className="bld-link__part">
          <span className="bld-link__cap">{bt('chrome.link.to', 'Link to')}</span>
          <div className="bld-link__dest">
            <div
              className="bld-seg"
              role="radiogroup"
              aria-label={btp('chrome.link.type_aria', '{name} target type', { name: field.label })}
            >
              {(['product', 'collection', 'page', 'external'] as LinkType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={current.type === t}
                  className="bld-seg__opt"
                  onClick={() => setType(t)}
                >
                  {t === 'external'
                    ? bt('chrome.link.type_url', 'URL')
                    : bt(`chrome.link.type_${t}`, t.charAt(0).toUpperCase() + t.slice(1))}
                </button>
              ))}
            </div>

            {current.type === 'external' ? (
              <input
                className="bld-input"
                type="text"
                aria-label={bt('chrome.link.external', 'External URL')}
                placeholder="https://example.com"
                value={current.url}
                onChange={(e) => {
                  const url = e.target.value;
                  if (isSafeExternal(url)) {
                    setExternalError(undefined);
                    onChange({ ...current, type: 'external', url });
                  } else {
                    setExternalError(
                      bt(
                        'chrome.link.scheme_error',
                        'Only http, https, mailto or /relative links are allowed.',
                      ),
                    );
                  }
                }}
              />
            ) : current.type === 'catalog' ? (
              // The catalog link (feature 0027) is a fixed target — no slug/url to edit. This control's
              // segmented options don't offer it (it's a navigation-menu concept), but a value set
              // elsewhere renders safely, as a read-only address in the target's own slot.
              <p className="bld-field__hint bld-link__fixed">
                {bt('chrome.link.all_products', 'All products · /collections/all')}
              </p>
            ) : current.type === 'policy' ? (
              // A legal policy page. Like the catalog above, it is a navigation-menu concept this control
              // does not offer — but a value set there must render as its address, not fall into the
              // picker branch below (there is no `PICKERS.policy`, so that branch would throw).
              <p className="bld-field__hint bld-link__fixed">/policies/{current.slug}</p>
            ) : (
              // The picker's key IS the slug: `resolveUrl` builds /products/<slug>, and an id there would
              // be a 404 nobody sees until the store is live.
              <EntityPickerListbox
                value={current.slug || undefined}
                onSelect={(slug) => onChange({ ...current, slug: slug ?? '' } as LinkValue)}
                source={PICKERS[current.type].source}
                label={field.label}
                noneLabel={bt('chrome.link.none', 'None')}
                searchPlaceholder={PICKERS[current.type].search}
                emptyLabel={PICKERS[current.type].empty}
                errorLabel={PICKERS[current.type].error}
              />
            )}
          </div>

          {externalError && (
            <p className="bld-field__error" role="alert">
              {externalError}
            </p>
          )}
        </div>

        {/* LABEL — a real caption, not just a placeholder: once the seller types in it, a placeholder is
            gone and the two boxes of this control are indistinguishable. */}
        <div className="bld-link__part">
          <label className="bld-link__cap" htmlFor={labelInputId}>
            {bt('chrome.link.label_aria', 'Link label')}
          </label>
          <input
            id={labelInputId}
            className="bld-input"
            type="text"
            placeholder={bt('chrome.link.label_ph', 'Button label (optional)')}
            value={current.label ?? ''}
            onChange={(e) => onChange({ ...current, label: e.target.value })}
          />
        </div>

        <button
          type="button"
          className="bld-switch bld-link__newtab"
          role="switch"
          aria-checked={current.newTab ?? false}
          aria-label={bt('chrome.link.new_tab', 'Open in new tab')}
          onClick={() => onChange({ ...current, newTab: !current.newTab })}
        >
          <span className="bld-switch__track" aria-hidden="true">
            <span className="bld-switch__knob" />
          </span>
          <span className="bld-link__newtab-text">
            {bt('chrome.link.new_tab', 'Open in new tab')}
          </span>
        </button>
      </div>
    </FieldRow>
  );
}
